// Whether the operator DECIDED the quote has no Note (packet 2A, C15, PM 22:46).
//
// A quote's Note has two stored lanes (the top-level quote_note, which the backend stores as NULL when empty, and the quote's own
// `note`). Both read back as empty for a Note nobody wrote and for one the operator deliberately emptied, and the Stage 5 seed
// turns an absent Note into '' on every open. So an empty Note says nothing about who emptied it, and automatic fallbacks (the
// client diagnosis hand-off in generation) fill it again.
//
// `meta.noteClearedByOperator` is the record of the decision. It is written only where the operator (or an accepted Assistant
// change) actually emptied a Note that had text, and removed when a Note is written again. It is NOT one of the readiness edit
// markers (`noteEditedByUser`, `quoteNoteEditedByUser`), which relax a diagnosis-note blocker (quote-issue-readiness.js): this
// one is read only by generation, and nothing here changes readiness. Passive hydration, sync and Save never write it.

export const NOTE_CLEARED_BY_OPERATOR = 'noteClearedByOperator';

type MetaLike = Record<string, any> | null | undefined;

const isBlank = (value: unknown): boolean => String(value ?? '').trim() === '';

/**
 * True when the quote's Note is empty AND the operator decided it should be. A later non-empty Note supersedes the decision.
 */
export function isNoteClearedByOperator(quote: { note?: unknown; meta?: MetaLike } | null | undefined): boolean {
    return quote?.meta?.[NOTE_CLEARED_BY_OPERATOR] === true && isBlank(quote?.note);
}

/**
 * The quote's meta after an authored change of its Note from `previousNote` to `nextNote`. Emptying a Note that had text records
 * the decision. Writing a non-empty Note removes it. Anything else (re-committing an empty field, an unchanged Note) returns the
 * same object, so a visit to the field is not a decision and creates no edit.
 */
export function metaAfterNoteEdit(meta: MetaLike, nextNote: unknown, previousNote: unknown): Record<string, any> | null | undefined {
    if (isBlank(nextNote)) {
        if (isBlank(previousNote) || meta?.[NOTE_CLEARED_BY_OPERATOR] === true) return meta;
        return { ...(meta || {}), [NOTE_CLEARED_BY_OPERATOR]: true };
    }
    if (!meta || meta[NOTE_CLEARED_BY_OPERATOR] === undefined) return meta;
    const { [NOTE_CLEARED_BY_OPERATOR]: _removed, ...rest } = meta;
    return rest;
}
